import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, Search, Edit, Trash2, Tag } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'sonner';
import CategoryFormDialog from '../../components/admin/CategoryFormDialog';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/collections`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      const categoryId = selectedCategory?.id || selectedCategory?._id;
      if (selectedCategory) {
        await axiosInstance.put(`${API_URL}/api/collections/${categoryId}`, categoryData);
        toast.success('Category updated successfully');
      } else {
        await axiosInstance.post(`${API_URL}/api/collections`, categoryData);
        toast.success('Category created successfully');
      }
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
      throw error;
    }
  };

  const handleDeleteCategory = async (category) => {
    const categoryId = category.id || category._id;
    if (window.confirm('Are you sure? This will affect all products in this category.')) {
      try {
        await axiosInstance.delete(`${API_URL}/api/collections/${categoryId}`);
        setCategories(categories.filter(c => (c.id || c._id) !== categoryId));
        toast.success('Category deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete category');
      }
    }
  };

  const filteredCategories = categories.filter(cat =>
    !searchQuery || cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Categories Management</h1>
              <p className="text-gray-600 mt-2">Manage product categories</p>
            </div>
            <Button className="btn-primary" onClick={() => { setSelectedCategory(null); setShowFormDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />Add Category
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input type="text" placeholder="Search categories..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCategories.map((category) => {
            const categoryId = category.id || category._id;
            return (
              <Card key={categoryId}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    {category.image ? (
                      <img src={category.image} alt={category.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-lg flex items-center justify-center">
                        <Tag className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{category.name}</h3>
                      {category.description && <p className="text-sm text-gray-600">{category.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 pt-4 border-t">
                    <Button variant="outline" size="sm" onClick={() => { setSelectedCategory(category); setShowFormDialog(true); }} className="flex-1">
                      <Edit className="w-4 h-4 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteCategory(category)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <CategoryFormDialog open={showFormDialog} onClose={() => setShowFormDialog(false)} category={selectedCategory} onSave={handleSaveCategory} />
    </div>
  );
}
